/**
 * LES DROITS DU REGISTRE DES ÉCARTS, côté écran — module PUR.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ MIROIR de `backend/src/nc-retenue.ts`. Le serveur fait foi : il refuse le
 * triage, l'écriture des rubriques, la clôture, la déclôture et jusqu'à la
 * lecture des pièces. Ces fonctions ne servent qu'à ne pas PROPOSER un geste
 * qui sera refusé — un bouton grisé sans phrase se lit comme une panne, et un
 * bouton actif qui échoue à l'appel est pire.
 *
 * ── Pourquoi un fichier, et non trois exports dans `NC.tsx` ────────────────
 *
 * C'est là qu'ils vivaient jusqu'au 08/10/2026, et c'était tenable tant que
 * seuls l'écran NC, le tableau de bord et le panneau des fiches liées les
 * lisaient. L'alignement de la déclôture des RÉCLAMATIONS a changé la donne :
 * `Reclamations.tsx` aurait dû importer `NC.tsx` — un écran entier, avec son
 * dossier, ses formulaires et ses propres imports — pour une comparaison de
 * deux chaînes. Ce dépôt a déjà payé ce défaut une fois (l'en-tête d'écran
 * sorti dans son propre fichier le 27/08/2026 pour cause de cycle d'import).
 *
 * ⚠️ UNE SEULE SOURCE ICI AUSSI, et des fonctions distinctes par-dessus — même
 * raison que côté serveur : le 08/10 au matin, deux listes séparées « parce que
 * la RQ peut vouloir reprendre l'un sans l'autre » ; le même jour elle a élargi
 * les deux d'un coup et y a ajouté la clôture. Des listes jumelles garantissent
 * qu'un quatrième acte en oublie une.
 */

/** Les rôles qui portent le registre des écarts. */
export const ROLES_REGISTRE_NC = ['rq', 'cq'];

const porte = (roles: string[] | null | undefined): boolean =>
  ROLES_REGISTRE_NC.some((r) => (roles || []).includes(r));

/**
 * OUVRIR le dossier d'une NC pas encore retenue : le RQ et le CQ. Voir sans
 * trancher — depuis le 13/10/2026 les deux ne sont plus la même question.
 */
export const peutOuvrirNCNonRetenue = (roles: string[]) => porte(roles);

/**
 * RETENIR ou rejeter d'office une déclaration (RQ, 13/10/2026) : le RQ, et qui
 * une casquette l'ouvre dans les rôles fins (acte `C13`, « Actes réglables »).
 *
 * ⚠️ Lit `session.actes.C13`, que le serveur calcule comme la garde d'actes
 * (`null` = ouvert, sinon la phrase du refus) : une personne à qui l'on accorde
 * le droit voit ses boutons sans qu'on touche à ce fichier. Repli sur le rôle
 * `rq` quand la session ne porte pas les actes (serveur plus ancien).
 */
export const peutTrancherNC = (roles: string[], actes?: Record<string, string | null> | null) =>
  actes && 'C13' in actes ? actes.C13 === null : (roles || []).includes('rq');

/**
 * Mener une NC de bout en bout : analyse, traitement, CLÔTURE.
 *
 * ⚠️ ÉLARGIT, ne substitue pas — le responsable du dossier mène toujours le
 * sien. D'où le `|| nc.responsable === session.key` partout où elle apparaît.
 */
export const tientLeRegistreNC = (roles: string[]) => porte(roles);

/**
 * Rouvrir un dossier scellé — non-conformité, et réclamation depuis le
 * 08/10/2026 (les deux déclôtures sont jumelles depuis le 14/08 : les laisser
 * diverger aurait donné deux journaux qu'un évaluateur ne peut pas comparer).
 *
 * ⚠️ La déclôture est le SEUL acte du registre des réclamations ouvert au CQ :
 * il n'y mène pas le dossier, il en défait seulement le sceau.
 */
export const peutDecloturerNC = (roles: string[]) => porte(roles);

/**
 * Modifier une réclamation (sa réponse, le retour au réclamant) : le CQ, comme le
 * RQ (RQ, 13/10/2026). Ne donne PAS les transitions d'état — le CQ modifie, il ne
 * mène pas le dossier. Le serveur fait foi (`peutModifierReclamation`).
 */
export const peutModifierReclamation = (roles: string[]) => porte(roles);
